// src/components/Changelog.jsx
import { useEffect, useState } from 'react';
import {
  Stack,
  Paper,
  Text,
  Title,
  Timeline,
  Group,
  ThemeIcon,
  Badge,
  List,
  LoadingOverlay,
  Box,
  Flex,
} from '@mantine/core';
import {
  IconRocket,
  IconBug,
  IconStar,
  IconHistory,
  IconCircleCheck,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import 'dayjs/locale/tr'; // Tarihleri Türkçe formatlamak için
import api from '../api/axiosConfig';

dayjs.locale('tr');

export default function Changelog() {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVersions();
  }, []);

  const fetchVersions = async () => {
    setLoading(true);
    try {
      // Endpoint yolunu kendi mimarinize göre güncelleyebilirsiniz
      const response = await api.get('/settings/versions/'); 
      setVersions(response.data || []);
    } catch (error) {
      console.error('Sürüm notları çekilemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => (date ? dayjs(date).format('DD MMMM YYYY') : '-');

  // En güncel sürümü (listede ilk sıradakini) tespit etmek için
  const isLatestVersion = (index) => index === 0;

  return (
    <Stack gap="xl" pos="relative" style={{ minHeight: '70vh' }}>
      <LoadingOverlay visible={loading} overlayProps={{ radius: 'sm', blur: 2 }} />

      {/* HERO SECTION */}
      <Paper
        radius="xl"
        p={{ base: 'lg', md: 'xl' }}
        style={{
          position: 'relative',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, rgba(15,23,42,0.98) 0%, rgba(30,41,59,0.95) 45%, rgba(59,130,246,0.90) 100%)',
          border: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Box
          style={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.05)',
          }}
        />
        
        <Flex justify="space-between" align="center" wrap="wrap" gap="md" style={{ position: 'relative', zIndex: 1 }}>
          <Group align="flex-start" wrap="nowrap">
            <ThemeIcon size={56} radius="xl" variant="white" color="rgba(255,255,255,0.12)">
              <IconHistory size={28} color="black" />
            </ThemeIcon>
            <div>
              <Text fw={700} size="sm" c="rgba(255,255,255,0.76)">
                SMS Grup Portal
              </Text>
              <Title order={2} c="white" fw={900} style={{ letterSpacing: '-0.5px' }}>
                Sürüm Notları
              </Title>
              <Text size="sm" maw={600} style={{ color: 'rgba(255,255,255,0.8)', mt: 4 }}>
                Sisteme eklenen yeni modüller, yapılan iyileştirmeler ve giderilen hatalar.
              </Text>
            </div>
          </Group>
        </Flex>
      </Paper>

      {/* TIMELINE (SÜRÜM LİSTESİ) */}
      <Paper radius="xl" p="xl" withBorder style={{ background: '#f8fafc' }}>
        {versions.length > 0 ? (
          <Timeline active={0} bulletSize={32} lineWidth={2}>
            {versions.map((version, index) => {
              const latest = isLatestVersion(index);

              return (
                <Timeline.Item
                  key={version.id}
                  title={
                    <Group gap="xs" mb={8} align="center">
                      <Title order={3} fw={900} c={latest ? 'blue.7' : 'dark.8'}>
                        {version.version_number}
                      </Title>
                      <Badge variant={latest ? 'filled' : 'light'} color={latest ? 'blue' : 'gray'} radius="sm">
                        {formatDate(version.release_date)}
                      </Badge>
                      {latest && (
                        <Badge color="green" variant="outline" radius="xl">
                          En Güncel Sürüm
                        </Badge>
                      )}
                    </Group>
                  }
                  bullet={<IconRocket size={16} />}
                  color={latest ? 'blue' : 'gray'}
                  lineVariant={latest ? 'solid' : 'dashed'}
                  pb="xl"
                >
                  <Stack gap="md" mt="sm">
                    <Text size="lg" fw={700} c="dark.7">
                      {version.title}
                    </Text>

                    {/* YENİ ÖZELLİKLER */}
                    {version.features && version.features.length > 0 && (
                      <Paper p="md" radius="md" withBorder bg="white">
                        <Group gap="xs" mb="sm">
                          <ThemeIcon color="green" variant="light" size="sm" radius="xl">
                            <IconStar size={14} />
                          </ThemeIcon>
                          <Text fw={700} c="green.8" size="sm">Yeni Özellikler</Text>
                        </Group>
                        <List spacing="xs" size="sm" icon={<IconCircleCheck size={16} color="var(--mantine-color-green-6)" />}>
                          {version.features.map((feature, i) => (
                            <List.Item key={i} c="dark.6">{feature}</List.Item>
                          ))}
                        </List>
                      </Paper>
                    )}

                    {/* İYİLEŞTİRMELER */}
                    {version.improvements && version.improvements.length > 0 && (
                      <Paper p="md" radius="md" withBorder bg="white">
                        <Group gap="xs" mb="sm">
                          <ThemeIcon color="violet" variant="light" size="sm" radius="xl">
                            <IconRocket size={14} />
                          </ThemeIcon>
                          <Text fw={700} c="violet.8" size="sm">İyileştirmeler</Text>
                        </Group>
                        <List spacing="xs" size="sm" icon={<IconCircleCheck size={16} color="var(--mantine-color-violet-6)" />}>
                          {version.improvements.map((improvement, i) => (
                            <List.Item key={i} c="dark.6">{improvement}</List.Item>
                          ))}
                        </List>
                      </Paper>
                    )}

                    {/* HATA ÇÖZÜMLERİ */}
                    {version.fixes && version.fixes.length > 0 && (
                      <Paper p="md" radius="md" withBorder bg="white">
                        <Group gap="xs" mb="sm">
                          <ThemeIcon color="red" variant="light" size="sm" radius="xl">
                            <IconBug size={14} />
                          </ThemeIcon>
                          <Text fw={700} c="red.8" size="sm">Hata Çözümleri</Text>
                        </Group>
                        <List spacing="xs" size="sm" icon={<IconCircleCheck size={16} color="var(--mantine-color-red-6)" />}>
                          {version.fixes.map((fix, i) => (
                            <List.Item key={i} c="dark.6">{fix}</List.Item>
                          ))}
                        </List>
                      </Paper>
                    )}
                  </Stack>
                </Timeline.Item>
              );
            })}
          </Timeline>
        ) : (
          !loading && (
            <Text c="dimmed" ta="center" py="xl">
              Henüz yayınlanmış bir sürüm notu bulunmuyor.
            </Text>
          )
        )}
      </Paper>
    </Stack>
  );
}